import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../../../lib/auth-context.js';
import { ScenarioDetailModal } from '../../scenario/components/ScenarioDetailModal.js';
import { ERD_IFRAME_URL } from '../erd-bridge.js';
import { useErdIframeBridge, type OpenScenarioPayload } from '../use-erd-iframe-bridge.js';

/**
 * Planning · ER Diagram. Renders an external WorkFocus visualization
 * iframe (CloudFront-hosted, separate repo) and acts as the parent
 * postMessage bridge — supplying the iframe with workspaces, users,
 * workspace metadata, and fusion-usage data on demand.
 *
 * Mirrors `wf-audit/src/slices/planning/pages/er-diagram.tsx`:
 *   - Iframe takes the whole content area.
 *   - When the iframe sends `OPEN_SCENARIO`, parent opens
 *     `ScenarioDetailModal` (the same modal the scenarios-list page
 *     uses for row detail). Legacy used `ScenarioPreviewDialog`, a
 *     thinner variant — we reuse the full modal so operators get a
 *     consistent experience.
 *   - Theme is dark (the new SPA is dark-only; legacy switched on
 *     `next-themes`).
 *
 * Workspace selection — primary or secondary — is implicit (primary
 * for now). A follow-up adds a dropdown above the iframe so operators
 * on multi-tenant licenses can switch.
 */
export function PlanningErDiagramPage(): JSX.Element {
  const { api } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [previewFusionId, setPreviewFusionId] = useState<string | null>(null);

  const handleOpenScenario = useCallback((payload: OpenScenarioPayload) => {
    if (!payload.fusionScenarioId) return;
    setPreviewFusionId(String(payload.fusionScenarioId));
  }, []);

  const { onIframeLoad } = useErdIframeBridge(iframeRef, {
    api,
    onOpenScenario: handleOpenScenario,
  });

  // `docsUrl` query is iframe-internal — points to the WorkFocus docs
  // site so links inside the diagram (the "?" in tooltips) resolve to
  // hosted documentation. The legacy `process.env.NEXT_PUBLIC_DOCS_URL`
  // becomes `import.meta.env.VITE_DOCS_URL`; absent on dev, the iframe
  // hides the help links rather than 404'ing.
  const docsUrl = (import.meta as { env?: { VITE_DOCS_URL?: string } }).env?.VITE_DOCS_URL ?? '';
  const docsParam = docsUrl ? `&docsUrl=${encodeURIComponent(`${docsUrl}/er-diagram`)}` : '';

  return (
    <div className="relative z-10 flex h-full flex-col">
      <iframe
        ref={iframeRef}
        onLoad={onIframeLoad}
        title="Planning ER Diagram"
        // `wcoeIframe=true` signals to the embedded app that it's
        // running as our embedded iframe (vs the standalone version
        // some Workfront customers use). `ver=7` is a cache-buster
        // bumped by the iframe app's owners; bump here when the
        // iframe app's deploy notes call it out. (Same number legacy
        // ships, kept in lockstep until the iframe app drops it.)
        src={`${ERD_IFRAME_URL}/?wcoeIframe=true&ver=7${docsParam}`}
        className="block h-full w-full border-none"
        style={{ minHeight: 'calc(100vh - 4rem)' }}
      />
      <ScenarioDetailModal
        fusionId={previewFusionId}
        listingRow={undefined}
        onClose={() => setPreviewFusionId(null)}
        role="developer"
      />
    </div>
  );
}
